import React, { useState } from 'react';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { useAuth } from '../context/AuthContext';
// We use standard HTML/CSS to prevent import errors
import { RefreshCw, LogOut, Copy, Terminal, Smartphone, Loader2, CheckCircle2 } from 'lucide-react';

export default function Dashboard() {
  const { status, qrCode, isLoading, refresh } = useWhatsApp();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const apiKey = user?.api_key || localStorage.getItem('api_key') || 'Not Generated';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 font-sans">
      
      {/* HEADER */}
      <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm border">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Console</h2>
          <p className="text-gray-500">Welcome, <span className="font-bold text-blue-600">@{user?.username || 'User'}</span></p>
        </div>
        <div className="flex gap-3 items-center">
            <button 
              onClick={refresh} 
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition"
            >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh Status
            </button>
            <div className={`px-4 py-2 rounded-full text-sm font-bold capitalize ${
              status === 'ready' ? 'bg-green-100 text-green-700' : 
              status === 'initializing' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
            }`}>
                {status || 'Unknown'}
            </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        
        {/* API KEY SECTION */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
             <Terminal className="w-5 h-5 text-blue-600" />
             <h3 className="font-bold text-lg">API Authentication</h3>
          </div>
          
          <div className="flex gap-2 mb-4">
            <input 
                value={apiKey} 
                readOnly 
                className="flex-1 bg-gray-50 border p-3 rounded font-mono text-sm text-gray-600 outline-none"
            />
            <button 
                onClick={copyToClipboard} 
                className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition"
            >
                {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          
          <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
            ⚠️ Keep this key secret. It allows full access to your WhatsApp account.
          </p>
        </div>

        {/* CONNECTION VISUALIZER */}
        <div className="bg-white p-0 rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row">
            <div className="p-6 flex-1 flex flex-col justify-center border-r border-gray-100">
                <h3 className="text-xl font-bold flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5" /> Link Device
                </h3>
                <p className="text-gray-500 text-sm mb-4">Scan the QR code with WhatsApp (Linked Devices).</p>
                
                {status === 'ready' && (
                    <button className="flex items-center gap-2 text-red-600 text-sm font-medium hover:underline">
                        <LogOut className="w-4 h-4" /> Disconnect Session
                    </button>
                )}
            </div>

            <div className="bg-gray-50 p-6 flex items-center justify-center flex-1 min-h-[250px]">
                {/* STATE 1: LOADING */}
                {status === 'initializing' ? (
                    <div className="text-center">
                        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
                        <p className="font-bold text-gray-700">Starting Engine...</p>
                        <p className="text-xs text-gray-500">Wait 15s for QR Code</p>
                    </div>
                ) 
                /* STATE 2: READY */
                : status === 'ready' ? (
                    <div className="text-center">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                            <CheckCircle2 className="w-8 h-8 text-white" />
                        </div>
                        <p className="font-bold text-green-700">System Online</p>
                    </div>
                )
                /* STATE 3: QR CODE */
                : qrCode ? (
                    <div className="bg-white p-2 rounded shadow-lg">
                        <img src={qrCode} alt="QR" className="w-48 h-48" />
                    </div>
                )
                /* STATE 4: FALLBACK */
                : (
                    <div className="flex flex-col items-center">
                        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                        <span className="text-gray-400 text-sm mt-2">Connecting...</span>
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}